import { useCallback, useEffect, useRef, useState } from 'react';
import { LeadFormActions } from '../../../Services/_actions';

const LEAD_LIST_PAGE_SIZE = 20;

export default function useLeadDetailsList({
  phone,
  campaignId,
  leadFormId,
  enabled,
  refreshToken = 0,
}) {
  const leadFormActions = LeadFormActions();
  const leadFormActionsRef = useRef(leadFormActions);
  leadFormActionsRef.current = leadFormActions;

  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isExhausted, setIsExhausted] = useState(false);

  const requestIdRef = useRef(0);
  const lastScrollTopRef = useRef(0);

  const hasMore = !isExhausted && leads.length < totalCount;

  useEffect(() => {
    if (!enabled || !campaignId || !leadFormId) {
      setLeads([]);
      setTotalCount(0);
      setNotFound(false);
      setIsExhausted(false);
      return;
    }

    let cancelled = false;
    const requestId = ++requestIdRef.current;
    lastScrollTopRef.current = 0;
    setIsExhausted(false);

    const loadInitial = async () => {
      setIsLoading(true);
      setNotFound(false);
      setLeads([]);
      setTotalCount(0);

      try {
        const response = await leadFormActionsRef.current.getB2b2CLeads({
          campaign_id: campaignId,
          leads_form_id: leadFormId,
          search: phone || '',
          limit: LEAD_LIST_PAGE_SIZE,
          offset: 0,
        });

        if (cancelled || requestId !== requestIdRef.current) return;

        const incoming = Array.isArray(response?.data?.list) ? response.data.list : [];
        const count = Number(response?.data?.count ?? incoming.length) || 0;

        setLeads(incoming);
        setTotalCount(count);
        setNotFound(incoming.length === 0);
        setIsExhausted(incoming.length === 0 || incoming.length >= count);
      } catch (err) {
        if (!cancelled && requestId === requestIdRef.current) {
          setLeads([]);
          setTotalCount(0);
          setNotFound(true);
          setIsExhausted(true);
        }
        console.error('Failed to load leads', err);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [enabled, campaignId, leadFormId, phone, refreshToken]);

  const loadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || isExhausted) return;
    if (leads.length >= totalCount) {
      setIsExhausted(true);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoadingMore(true);

    try {
      const response = await leadFormActionsRef.current.getB2b2CLeads({
        campaign_id: campaignId,
        leads_form_id: leadFormId,
        search: phone || '',
        limit: LEAD_LIST_PAGE_SIZE,
        offset: leads.length,
      });

      if (requestId !== requestIdRef.current) return;

      const incoming = Array.isArray(response?.data?.list) ? response.data.list : [];
      const count = Number(response?.data?.count ?? totalCount) || totalCount;

      setTotalCount(count);

      if (incoming.length === 0) {
        setIsExhausted(true);
        return;
      }

      setLeads((prev) => {
        const seen = new Set(prev.map((lead) => lead.lead_id || lead.id));
        const merged = [...prev];
        incoming.forEach((lead) => {
          const key = lead.lead_id || lead.id;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(lead);
          }
        });
        return merged;
      });

      if (leads.length + incoming.length >= count) {
        setIsExhausted(true);
      }
    } catch (err) {
      console.error('Failed to load more leads', err);
      setIsExhausted(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [campaignId, leadFormId, phone, isLoading, isLoadingMore, isExhausted, leads.length, totalCount]);

  const handleScroll = useCallback(
    (event) => {
      if (!hasMore || isLoading || isLoadingMore) return;

      const target = event.currentTarget;
      const scrollTop = target.scrollTop;

      if (scrollTop <= lastScrollTopRef.current) return;
      lastScrollTopRef.current = scrollTop;

      const bottomReached =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 48;

      if (bottomReached) {
        loadMore();
      }
    },
    [hasMore, isLoading, isLoadingMore, loadMore]
  );

  return {
    leads,
    isLoading,
    isLoadingMore,
    hasMore,
    notFound,
    handleScroll,
  };
}
